import React, { useState } from 'react';
import { FormFieldCorrespondingAuthor } from '@publishing-engine/domain/types/article';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { EntryPoint } from 'src/utils/types';
import AuthorCard from 'src/pages/Organization/public/Journal/SubmissionUpload/components/AuthorCard';
import { AuthorEditModal } from 'src/pages/Organization/public/Journal/SubmissionUpload/components/AuthorEditModal';
import { CorrespondingAuthorDisplayDiffModal } from './CorrespondingAuthorDisplayDiffModal';
import { FieldHeader } from '../common/FieldHeader';
import { IAvailableWorkflowActions } from '@publishing-engine/domain/models';
import { CorrespondingAuthorEmptyState } from './CorrespondingAuthorEmptyState';

type IProps = {
  formFieldDefinition: FormFieldCorrespondingAuthor;
  value: string;
  previousValue?: string;
  onChange: (value: string) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
  availableActions: IAvailableWorkflowActions;
};

export const CorrespondingAuthorFiled: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  onChange,
  entryPoint,
  isUpdateRequired,
  previousValue,
  availableActions
}) => {
  const { getFieldState, checkFormFieldValueIsUpdated } =
    useSubmissionFields(entryPoint);
  const authors = getFieldState('Authors')?.value as any;
  const authorDetails = authors.find((author: any) => author.email === value);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    params: { labels }
  } = formFieldDefinition;
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const handleSaveChanges = async (selectedEmail: string) => {
    onChange(selectedEmail);
    setIsEditing(false);
  };
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const isEditable = !!onChange;

  const handleEditClick = (isEditing: boolean) => {
    if (isEditing) {
      setIsModalOpen(true);
    } else {
      setIsEditing(isEditing);
    }
  };
  const handleModalClose = () => {
    setIsModalOpen(false);
  };
  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);
  const handleModalConfirm = () => {
    setIsEditing(true);
    setIsModalOpen(false);
  };

  return (
    <>
      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        isUpdated={isUpdated}
        editCallback={() => {
          setIsModalOpen(true);
        }}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
        tooltipText={labels.subtitle}
      />{' '}
      {authorDetails ? (
        <AuthorCard
          title={labels.main}
          subtitle={labels.subtitle}
          isEditing={isEditing}
          showOrcid={false}
          showTitleAndEditButton={false}
          user={{
            ...authorDetails,
            affiliationDetails: {
              organizationName: authorDetails?.affiliation,
              ringgoldId: authorDetails?.ringgoldId
            }
          }}
          options={authors.map((author: any) => ({
            value: author.email,
            label: `${author.firstName} ${author.lastName}`
          }))}
          defaultValue={value}
          onChange={handleSaveChanges}
          onEdit={handleEditClick}
          shouldShowOutline={false}
          disabled={!isEditable || !isUpdateRequired}
          isUpdateRequired={isUpdateRequired}
        />
      ) : (
        <CorrespondingAuthorEmptyState />
      )}
      {showDiffModal && (
        <CorrespondingAuthorDisplayDiffModal
          previousValue={previousValue}
          value={value}
          config={formFieldDefinition}
          onClose={closeDiffModal}
          authors={authors}
        />
      )}
      {isModalOpen && (
        <AuthorEditModal
          title="Edit Corresponding Author?"
          description="This role manages important submission communications and commitments.
                Ensure consensus among all co-authors before making this change to
                avoid potential conflicts."
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
        />
      )}
    </>
  );
};
